const defineConnector = ({
  id,
  label,
  description,
  tagline,
  iconPath,
  includes,
  capabilities,
  contentTypes,
}) => ({
  id,
  label,
  description,
  tagline,
  iconPath,
  includes,
  capabilities,
  contentTypes,
});

const CONNECTOR_DEFINITIONS = [
  {
    id: 'slack',
    label: 'Slack',
    description: 'Workspace messages, threads, and shared files for ongoing creative context.',
    tagline: 'Bring decisions and discussion threads into the agent.',
    iconPath: '/connector-icons/slack.svg',
    includes: ['Channels', 'Threads', 'Shared files', 'Image attachments'],
    capabilities: ['messages', 'threads', 'files', 'images', 'video'],
    contentTypes: ['message', 'thread', 'file', 'image', 'video', 'link'],
  },
  {
    id: 'google-workspace',
    label: 'Google Workspace',
    description: 'Drive folders, Docs, Slides, and Sheets used across briefs and brand documents.',
    tagline: 'Pull in working docs, decks, and sheets from Drive.',
    iconPath: '/connector-icons/google-workspace.svg',
    includes: ['Drive files', 'Docs', 'Slides', 'Sheets'],
    capabilities: ['docs', 'slides', 'sheets', 'files', 'images'],
    contentTypes: ['document', 'presentation', 'spreadsheet', 'file', 'image', 'video', 'link'],
  },
  {
    id: 'microsoft-365',
    label: 'Microsoft 365',
    description: 'SharePoint, OneDrive, Word, PowerPoint, and Excel files from your tenant.',
    tagline: 'Connect Microsoft 365 documents and shared storage.',
    iconPath: '/connector-icons/microsoft-365.svg',
    includes: ['SharePoint', 'OneDrive', 'Word', 'PowerPoint', 'Excel'],
    capabilities: ['docs', 'slides', 'sheets', 'files', 'email'],
    contentTypes: ['document', 'presentation', 'spreadsheet', 'file', 'email', 'image', 'video', 'link'],
  },
  {
    id: 'notion',
    label: 'Notion',
    description: 'Brand bibles, campaign briefs, tone guides, roadmaps, and team databases.',
    tagline: 'Surface the workspace pages your team writes in.',
    iconPath: '/connector-icons/notion.svg',
    includes: ['Pages', 'Databases', 'Attachments'],
    capabilities: ['pages', 'databases', 'docs', 'files'],
    contentTypes: ['document', 'database', 'file', 'image', 'link'],
  },
  {
    id: 'figma',
    label: 'Figma',
    description: 'Design files, variables, components, and FigJam boards for visual context.',
    tagline: 'Reference the design system and in-flight files.',
    iconPath: '/connector-icons/figma.svg',
    includes: ['Files', 'Components', 'Variables', 'FigJam'],
    capabilities: ['design files', 'components', 'variables', 'boards'],
    contentTypes: ['design', 'board', 'component', 'image', 'link'],
  },
  {
    id: 'canva',
    label: 'Canva',
    description: 'Brand kits, templates, social layouts, and campaign decks.',
    tagline: 'Reach your Canva brand kits and templates.',
    iconPath: '/connector-icons/canva.svg',
    includes: ['Brand kits', 'Templates', 'Designs'],
    capabilities: ['brand kits', 'templates', 'designs'],
    contentTypes: ['design', 'template', 'brand-kit', 'image', 'video', 'link'],
  },
  {
    id: 'adobe-creative-cloud',
    label: 'Adobe',
    description: 'Adobe Creative Cloud files, templates, and brand asset libraries.',
    tagline: 'Use Creative Cloud assets and Firefly references.',
    iconPath: '/connector-icons/adobe-creative-cloud.svg',
    includes: ['Libraries', 'Files', 'Brand assets'],
    capabilities: ['libraries', 'files', 'brand assets'],
    contentTypes: ['library', 'file', 'image', 'video', 'template', 'link'],
  },
  {
    id: 'cloudinary',
    label: 'Cloudinary',
    description: 'Media assets, transformations, tags, and delivery-ready references.',
    tagline: 'Tap into managed media in your Cloudinary library.',
    iconPath: '/connector-icons/cloudinary.svg',
    includes: ['Images', 'Video', 'Folders', 'Tags'],
    capabilities: ['images', 'video', 'folders', 'tags'],
    contentTypes: ['image', 'video', 'folder', 'tag', 'link'],
  },
];

export const CONTEXT_CONNECTORS = Object.freeze(CONNECTOR_DEFINITIONS.map(defineConnector));
export const CONTEXT_CONNECTOR_IDS = Object.freeze(CONTEXT_CONNECTORS.map((connector) => connector.id));

const CONNECTOR_BY_ID = new Map(CONTEXT_CONNECTORS.map((connector) => [connector.id, connector]));

const CONNECTOR_ALIASES = new Map([
  ['drive', 'google-workspace'],
  ['google-drive', 'google-workspace'],
  ['google-docs', 'google-workspace'],
  ['google-sheets', 'google-workspace'],
  ['google-slides', 'google-workspace'],
  ['workspace', 'google-workspace'],
  ['gdrive', 'google-workspace'],
  ['google', 'google-workspace'],
  ['office', 'microsoft-365'],
  ['office-365', 'microsoft-365'],
  ['o365', 'microsoft-365'],
  ['sharepoint', 'microsoft-365'],
  ['onedrive', 'microsoft-365'],
  ['outlook', 'microsoft-365'],
  ['microsoft', 'microsoft-365'],
  ['teams', 'microsoft-365'],
  ['ms-teams', 'microsoft-365'],
  ['microsoft-teams', 'microsoft-365'],
  ['microsoft-teams-chat', 'microsoft-365'],
  ['figjam', 'figma'],
  ['aem', 'adobe-creative-cloud'],
  ['aem-assets', 'adobe-creative-cloud'],
  ['adobe-assets', 'adobe-creative-cloud'],
  ['adobe-experience-manager', 'adobe-creative-cloud'],
  ['creative-cloud', 'adobe-creative-cloud'],
  ['adobe', 'adobe-creative-cloud'],
]);

const TEXT_MATCHERS = [
  { id: 'slack', pattern: /\bslack\b|slack\.com/i },
  { id: 'google-workspace', pattern: /\bgoogle drive\b|\bgdrive\b|docs\.google\.com|drive\.google\.com|\bgoogle workspace\b/i },
  { id: 'microsoft-365', pattern: /\bmicrosoft 365\b|\boffice 365\b|\bsharepoint\b|\bonedrive\b|\bteams\b|teams\.microsoft\.com/i },
  { id: 'notion', pattern: /\bnotion\b|notion\.so/i },
  { id: 'figma', pattern: /\bfigma\b|\bfigjam\b|figma\.com/i },
  { id: 'canva', pattern: /\bcanva\b|canva\.com/i },
  { id: 'adobe-creative-cloud', pattern: /\badobe creative cloud\b|\bcreative cloud\b|\badobe\b/i },
  { id: 'cloudinary', pattern: /\bcloudinary\b|cloudinary\.com/i },
];

function slugifyConnectorId(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/['’]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function normalizeContextConnectorId(value) {
  const slug = slugifyConnectorId(value);

  if (!slug) {
    return null;
  }

  if (CONNECTOR_BY_ID.has(slug)) {
    return slug;
  }

  return CONNECTOR_ALIASES.get(slug) || null;
}

function inferContextConnectorId(value) {
  const directId = normalizeContextConnectorId(value);
  if (directId) {
    return directId;
  }

  const text = typeof value === 'string' ? value : '';
  if (!text) {
    return null;
  }

  return TEXT_MATCHERS.find((matcher) => matcher.pattern.test(text))?.id || null;
}

function normalizeConnectorIdList(value) {
  const sourceIds = Array.isArray(value) ? value : [];
  const normalizedIds = [];
  const seenIds = new Set();

  for (const sourceId of sourceIds) {
    const connectorId = normalizeContextConnectorId(sourceId);
    if (!connectorId || seenIds.has(connectorId)) {
      continue;
    }
    seenIds.add(connectorId);
    normalizedIds.push(connectorId);
  }

  return normalizedIds;
}

export function normalizeAddedContextConnectorIds(value = []) {
  return normalizeConnectorIdList(value);
}

export function normalizeEnabledContextConnectorIds(value = []) {
  return normalizeConnectorIdList(value);
}

export function getContextConnectorById(connectorId) {
  const normalizedId = normalizeContextConnectorId(connectorId);
  return normalizedId ? CONNECTOR_BY_ID.get(normalizedId) || null : null;
}

function collectCandidateReferences(value, collector) {
  if (!value) {
    return;
  }

  if (Array.isArray(value)) {
    value.forEach((item) => collectCandidateReferences(item, collector));
    return;
  }

  if (typeof value === 'string') {
    collector(value);
    return;
  }

  if (typeof value !== 'object') {
    return;
  }

  collector(value.connectorId);
  collector(value.connector);
  collector(value.connectorName);
  collector(value.sourceId);
  collector(value.source);
  collector(value.sourceName);
  collector(value.provider);
  collector(value.service);
  collector(value.type);
  collector(value.name);
  collector(value.label);
  collector(value.url);
  collector(value.href);
}

export function collectContextConnectorIdsFromSourceMetadata(part = {}) {
  const ids = [];
  const seenIds = new Set();
  const addReference = (value) => {
    const connectorId = inferContextConnectorId(value);
    if (!connectorId || seenIds.has(connectorId)) {
      return;
    }
    seenIds.add(connectorId);
    ids.push(connectorId);
  };

  collectCandidateReferences(part.connectorSourceIds, addReference);
  collectCandidateReferences(part.connectorSources, addReference);
  collectCandidateReferences(part.sources, addReference);
  collectCandidateReferences(part.source, addReference);
  collectCandidateReferences(part.citations, addReference);
  collectCandidateReferences(part.metadata?.connectorSourceIds, addReference);
  collectCandidateReferences(part.metadata?.connectorSources, addReference);
  collectCandidateReferences(part.metadata?.sources, addReference);
  collectCandidateReferences(part.providerMetadata?.glowghost?.connectorSourceIds, addReference);
  collectCandidateReferences(part.providerMetadata?.glowghost?.connectorSources, addReference);
  collectCandidateReferences(part.experimental_providerMetadata?.glowghost?.connectorSourceIds, addReference);
  collectCandidateReferences(part.experimental_providerMetadata?.glowghost?.connectorSources, addReference);

  return ids;
}
